import { useMemo, useEffect, useCallback, useState } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import { STUDENT_INTERNSHIPS_QUERY } from "../internships/queries";
import { UNIVERSITY_QUERY } from "../queries";
import { GET_ACTIVE_UNIVERSITY_PROBATIONS_QUERY } from "../students/probations/queries";
import { STUDENTS_SEMESTER_REPORT_QUERY } from "./queries";
import {
  buildInternshipMonthsByPgaId,
  buildReportColumns,
  buildSemesterReportCsvRows,
  buildTournamentProbationByPersonId,
  mapUniversityReportRows,
  semesterReportCsvFilename
} from "./_utils";
import { downloadCsv } from "./downloadCsv";
import { fetchAllStudentsForExport } from "./exportSemesterCsv";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";

type ActiveProbationNode = {
  id: string;
  startedAt: string;
  active: boolean;
  probationType?: string | null;
  person: { id: string };
};

type ProbationsLoadState = "idle" | "loading" | "loaded" | "failed";

const EMPTY_STUDENTS_CONNECTION = {
  totalCount: 0,
  pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: undefined, endCursor: undefined },
  nodes: []
};

export default function UniversitySemesterReportPage() {
  const location = useLocation();
  const { universityId } = useParams();

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams();
  const [activeProbationNodes, setActiveProbationNodes] = useState<ActiveProbationNode[]>([]);
  const [probationsLoadState, setProbationsLoadState] = useState<ProbationsLoadState>("idle");
  const [exporting, setExporting] = useState(false);

  const { data: universityData } = useQuery(UNIVERSITY_QUERY, {
    variables: { legacyId: universityId || "" },
    skip: !universityId
  });
  const universityUuid = universityData?.universities?.[0]?.id;

  const [getActiveProbationsPage] = useLazyQuery(GET_ACTIVE_UNIVERSITY_PROBATIONS_QUERY, {
    errorPolicy: "all",
    fetchPolicy: "network-only"
  });
  const [getStudentsForExport] = useLazyQuery(STUDENTS_SEMESTER_REPORT_QUERY, {
    fetchPolicy: "network-only"
  });

  const loadActiveProbations = useCallback(
    async (selectedUniversityUuid: string) => {
      const allNodes: ActiveProbationNode[] = [];
      let hasNextPage = true;
      let after: string | undefined;
      const pageSize = 100;
      const maxPages = 50;
      let pageCount = 0;
      let loadSucceeded = true;

      setProbationsLoadState("loading");

      try {
        while (hasNextPage && pageCount < maxPages) {
          pageCount += 1;
          const { data, error } = await getActiveProbationsPage({
            variables: {
              universityId: selectedUniversityUuid,
              first: pageSize,
              after
            }
          });

          const connection = data?.university?.probations;
          if (error || !data?.university || !connection?.nodes) {
            loadSucceeded = false;
            break;
          }

          allNodes.push(...connection.nodes);
          hasNextPage = connection.pageInfo?.hasNextPage || false;
          after = connection.pageInfo?.endCursor || undefined;
        }

        if (loadSucceeded) {
          setActiveProbationNodes(allNodes);
          setProbationsLoadState("loaded");
        } else {
          setActiveProbationNodes([]);
          setProbationsLoadState("failed");
        }
      } catch {
        setActiveProbationNodes([]);
        setProbationsLoadState("failed");
      }
    },
    [getActiveProbationsPage]
  );

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  useEffect(() => {
    if (!universityUuid) return;

    void loadActiveProbations(universityUuid);
  }, [universityUuid, loadActiveProbations]);

  const { data, loading } = useQuery(STUDENTS_SEMESTER_REPORT_QUERY, {
    variables: {
      ...paginationVariables,
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: debouncedSearch || undefined
      }
    },
    skip: !universityId
  });
  const { data: internshipsData, loading: internshipsLoading } = useQuery(STUDENT_INTERNSHIPS_QUERY, {
    variables: {
      first: 10000,
      input: {
        universityId: universityId?.toString() || "",
        alumni: false
      }
    },
    skip: !universityId
  });

  const canResolveTournamentRequirements = probationsLoadState === "loaded";
  const probationsLoading = probationsLoadState === "loading";

  const tournamentProbationByPersonId = useMemo(
    () => buildTournamentProbationByPersonId(activeProbationNodes),
    [activeProbationNodes]
  );

  const reportRows = useMemo(
    () =>
      mapUniversityReportRows(
        data?.universityStudents || EMPTY_STUDENTS_CONNECTION,
        buildInternshipMonthsByPgaId(internshipsData),
        tournamentProbationByPersonId,
        undefined,
        canResolveTournamentRequirements
      ),
    [data, internshipsData, tournamentProbationByPersonId, canResolveTournamentRequirements]
  );
  const columns = useMemo(() => buildReportColumns(), []);

  const encodedReturnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
  const tableLoading = loading || internshipsLoading || probationsLoading;

  const handleExportCsv = useCallback(async () => {
    if (exporting || !universityId) return;

    setExporting(true);
    try {
      const { nodes, truncated } = await fetchAllStudentsForExport({
        fetchPage: async (options) => {
          const result = await getStudentsForExport(options);
          return {
            data: result.data,
            error: result.error as Error | undefined
          };
        },
        universityLegacyId: universityId.toString(),
        search: debouncedSearch || undefined
      });

      const exportConnection = {
        ...EMPTY_STUDENTS_CONNECTION,
        totalCount: nodes.length,
        nodes
      };
      const rows = mapUniversityReportRows(
        exportConnection,
        buildInternshipMonthsByPgaId(internshipsData),
        tournamentProbationByPersonId,
        undefined,
        canResolveTournamentRequirements
      );

      if (rows.length === 0) {
        showSnackbar("No students to export", { variant: "info" });
        return;
      }

      const noun = rows.length === 1 ? "student" : "students";
      downloadCsv(buildSemesterReportCsvRows(rows), semesterReportCsvFilename());
      showSnackbar(
        truncated
          ? `Exported ${rows.length} ${noun} (page limit reached; some rows may be missing)`
          : `Exported ${rows.length} ${noun}`
      );
    } catch {
      showErrorSnackbar("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  }, [
    exporting,
    universityId,
    getStudentsForExport,
    debouncedSearch,
    internshipsData,
    tournamentProbationByPersonId,
    canResolveTournamentRequirements
  ]);

  return (
    <>
      <TableHeading
        heading="University Student Progress Report"
        body="Roster-style report view for university administrators."
        actions={[
          {
            label: exporting ? "Exporting…" : "Export CSV",
            icon: "download",
            variant: "text",
            disabled: exporting || tableLoading,
            onClick: () => {
              void handleExportCsv();
            },
            testId: "export-semester-report-csv-button"
          }
        ]}
      />
      <Table
        data={reportRows}
        loading={tableLoading}
        columns={columns}
        data-testid="students-report-table"
        hasActiveFilters={!!debouncedSearch}
        getRowLink={(row) => ({
          to: `/education/universities/${universityId}/students/${row.original.studentId}?returnUrl=${encodedReturnUrl}`
        })}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls pagination={createHeaderPagination(data?.universityStudents)} onSortClick={onSortClick}>
            <SearchInput
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
              placeholder="Search Students"
            />
          </TableControls>
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.universityStudents)} />}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Students in Report"
            caption="There are no current students available for this university report."
            iconName="school"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Matching Students"
            caption="Try adjusting your search to find students."
            iconName="search"
          />
        )}
      />
    </>
  );
}
